import { useCallback, useState } from "react";

import { ApiError } from "../api/client";
import { StepUpDialog } from "./StepUpDialog";

interface PendingAct {
  /** What to do again once a code has been confirmed. */
  replay: () => void;
  /** Whether the account has a code at all, as the refusal reported it. */
  enrolled: boolean;
  /** The server's own sentence, shown in the dialog unchanged. */
  message: string;
}

export interface StepUpGate {
  /**
   * Take over a refusal that asked for a code.
   *
   * Args:
   *     error: What the mutation failed with.
   *     replay: The same act, to run again once the code is confirmed.
   *
   * Returns:
   *     True when this gate took the refusal — the caller then shows nothing
   *     of its own, because the dialog is the answer.
   */
  capture: (error: unknown, replay: () => void) => boolean;
  /** The dialog to render, or null when nothing is waiting on a code. */
  dialog: JSX.Element | null;
}

/**
 * Ask for the code, then do the thing again.
 *
 * Four acts are gated (`docs/API.md`): widening a consent, requesting a
 * deletion, approving an account, minting a console key. Each answers
 * `403 step_up_required` until a code has been confirmed in the last
 * `step_up_minutes`. Every screen that performs one of them calls `capture`
 * from its mutation's `onError` and renders `dialog`; the act is replayed on
 * success, so nobody fills a form in twice.
 *
 * The prompt exists because the server refused, never because this page
 * guessed it would: `/me` publishes when the window is open so a screen can
 * *warn* beforehand, but the refusal is what opens the dialog.
 *
 * Returns:
 *     The capture function and the dialog to render.
 */
export function useStepUp(): StepUpGate {
  const [pending, setPending] = useState<PendingAct | null>(null);

  const capture = useCallback(
    (error: unknown, replay: () => void): boolean => {
      if (!(error instanceof ApiError) || !error.isStepUpRequired) {
        return false;
      }
      setPending({ replay, enrolled: error.isEnrolled, message: error.message });
      return true;
    },
    [],
  );

  const dialog = pending ? (
    <StepUpDialog
      enrolled={pending.enrolled}
      serverMessage={pending.message}
      onConfirmed={() => {
        const act = pending.replay;
        setPending(null);
        act();
      }}
      onClose={() => setPending(null)}
    />
  ) : null;

  return { capture, dialog };
}
